import "../index.css";
import { useEffect, useState } from "react";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useNavigate } from "react-router-dom";

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: { section: string; items: FaqItem[] }[] = [
  {
    section: "Getting Started",
    items: [
      {
        question: "What is Stake Alerts?",
        answer:
          "Stake Alerts is a sports betting alert platform that monitors prop betting opportunities across multiple sportsbooks and notifies you when your custom criteria are met — so you never miss a line you're looking for.",
      },
      {
        question: "How do I create an alert?",
        answer:
          "After logging in, go to the Rules page and tap 'Create Rule'. Choose a sport, player, stat, and your target value. Once saved, we'll alert you whenever that condition is met.",
      },
      {
        question: "Which sportsbooks are supported?",
        answer:
          "We monitor lines from the major US sportsbooks. Coverage continues to expand — check the app for the current list.",
      },
    ],
  },
  {
    section: "Account",
    items: [
      {
        question: "How do I reset my password?",
        answer:
          "On the login screen, tap 'Forgot Password' and enter your email address. You'll receive a reset link within a few minutes. You can also request a reset from the Profile page once logged in.",
      },
      {
        question: "I didn't receive my verification email. What should I do?",
        answer:
          "Check your spam or junk folder first. If it's not there, log in and visit the verification page — there's a 'Resend Verification Email' button. If you still have trouble, contact us.",
      },
      {
        question: "How do I change my display name?",
        answer:
          "Go to Settings and tap the edit icon next to your username. Enter your new name and save.",
      },
      {
        question: "How do I delete my account?",
        answer:
          "Go to Settings and scroll to the bottom. Tap 'Delete Account', type DELETE to confirm, and submit. Your account and all associated data will be removed. If you have an active subscription, please cancel it first to avoid future charges.",
      },
    ],
  },
  {
    section: "Subscriptions & Billing",
    items: [
      {
        question: "What plans are available?",
        answer:
          "We offer Basic, Pro, and Premium plans on monthly and annual billing cycles. Annual plans offer a significant discount over monthly. A free trial is available for new users.",
      },
      {
        question: "How do I cancel my subscription?",
        answer:
          "Go to Settings → Update Subscription and tap 'Cancel Subscription'. Your access continues until the end of the current billing period.",
      },
      {
        question: "How do I update my payment method?",
        answer:
          "Go to Settings and tap 'Update Payment'. You'll be taken to our secure billing portal where you can update your card details.",
      },
      {
        question: "Do you offer refunds?",
        answer:
          "We don't offer refunds for partial billing periods, but you'll always retain access until the end of any period you've paid for. If you believe you were charged in error, contact us and we'll look into it.",
      },
      {
        question: "I was charged after cancelling. What happened?",
        answer:
          "Cancellation stops future renewals but does not immediately end your current billing period. If you were charged after your access should have ended, please contact us with your account email and we'll investigate.",
      },
    ],
  },
  {
    section: "Technical",
    items: [
      {
        question: "The app isn't loading. What should I try?",
        answer:
          "Try force-closing and reopening the app. If the issue persists, check your internet connection, then try uninstalling and reinstalling. If it still doesn't work, contact us with a description of what you see.",
      },
      {
        question: "I'm not receiving alerts. Why?",
        answer:
          "Make sure notifications are enabled for Stake Alerts in your device settings. Also confirm your rules are active on the Rules page. If everything looks correct, contact us.",
      },
    ],
  },
];

function FaqSection({ section, items }: { section: string; items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div style={{ marginBottom: 36 }}>
      <h3
        className="font_family_base"
        style={{
          fontSize: 18,
          fontWeight: "bold",
          color: "#aaa",
          textTransform: "uppercase",
          letterSpacing: 1,
          marginBottom: 12,
          borderBottom: "1px solid #333",
          paddingBottom: 8,
        }}
      >
        {section}
      </h3>
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            borderBottom: "1px solid #222",
          }}
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="font_family_base"
            style={{
              width: "100%",
              textAlign: "left",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "14px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              color: "inherit",
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              {item.question}
            </span>
            <span style={{ fontSize: 20, color: "#888", flexShrink: 0 }}>
              {openIndex === i ? "−" : "+"}
            </span>
          </button>
          {openIndex === i && (
            <p
              className="font_family_base detail_text"
              style={{
                fontSize: 14,
                lineHeight: 1.7,
                paddingBottom: 14,
                paddingRight: 24,
                margin: 0,
              }}
            >
              {item.answer}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Support() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div className="center_me">
          <div style={{ maxWidth: 720, width: "100%", padding: "20px" }}>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <h2
                className="font_family_base header_text"
                style={{ fontSize: 32, fontWeight: "bold", margin: 0 }}
              >
                Support
              </h2>
              <p
                className="font_family_base detail_text"
                style={{ fontSize: 15, marginTop: 12, lineHeight: 1.6 }}
              >
                Find answers to common questions below. Can't find what you
                need?{" "}
                <button
                  onClick={() => navigate("/contact")}
                  className="font_family_base"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#4a9eff",
                    fontSize: 15,
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Contact us
                </button>{" "}
                and we'll get back to you.
              </p>
            </div>

            {faqs.map((group) => (
              <FaqSection
                key={group.section}
                section={group.section}
                items={group.items}
              />
            ))}

            <div
              style={{
                marginTop: 48,
                padding: "24px",
                borderRadius: 12,
                border: "1px solid #333",
                textAlign: "center",
              }}
            >
              <p
                className="font_family_base"
                style={{ fontSize: 16, fontWeight: "bold", marginBottom: 8 }}
              >
                Still need help?
              </p>
              <p
                className="font_family_base detail_text"
                style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}
              >
                Our support team typically responds within 1 business day.
              </p>
              <button
                onClick={() => navigate("/contact")}
                className="font_family_base"
                style={{
                  background: "none",
                  border: "1px solid #4a9eff",
                  borderRadius: 8,
                  color: "#4a9eff",
                  fontSize: 14,
                  padding: "10px 24px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Contact Support
              </button>
            </div>

            {/* <div
              style={{
                marginTop: 32,
                display: "flex",
                gap: 24,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => navigate("/privacy")}
                className="font_family_base"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#666",
                  fontSize: 13,
                  textDecoration: "underline",
                  padding: 0,
                }}
              >
                Privacy Policy
              </button>
              <button
                onClick={() => navigate("/terms")}
                className="font_family_base"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#666",
                  fontSize: 13,
                  textDecoration: "underline",
                  padding: 0,
                }}
              >
                Terms of Service
              </button>
            </div> */}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
